import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Plus } from 'lucide-react';
import InterviewProfilesTab from './InterviewProfilesTab';
import QuestionBank from '../questionBank/QuestionBank';

const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

const tabButtonClass = (isActive) =>
  `px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
    isActive
      ? 'text-white shadow-sm'
      : 'text-slate-600 hover:bg-white hover:text-slate-900'
  }`;

export default function InterviewLibrary() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profiles'); // 'profiles' | 'bank'
  const [selectedCompany, setSelectedCompany] = useState('');
  const [triggerCreateProfile, setTriggerCreateProfile] = useState(0);
  const [triggerAddQuestion, setTriggerAddQuestion] = useState(0);

  return (
    <div
      className="h-[calc(100vh-64px)] flex flex-col bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30"
      style={{ paddingTop: '12px' }}
    >
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex flex-col min-h-0 pb-3">
        <div className="bg-white rounded-xl flex flex-col flex-1 min-h-0 shadow-xs border border-gray-200/90 overflow-hidden">
          {/* Header Banner - Matching Aptiview Theme Gradient */}
          <div
            className="px-5 py-3 text-white rounded-t-xl shadow-xs shrink-0"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
              <div>
                <h1 className="text-base sm:text-lg font-bold leading-tight text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-white stroke-[2.3]" />
                  <span>{t('interviewLibrary.title', 'Interview Library')}</span>
                </h1>
                <p className="text-[11px] sm:text-xs text-blue-100 mt-0.5">
                  {t(
                    'interviewLibrary.pageSubtitle',
                    'Standardize your recruitment with role-based question profiles and reusable company question bank'
                  )}
                </p>
              </div>

              {/* Action Button on Header */}
              <div className="flex items-center gap-2">
                {activeTab === 'profiles' ? (
                  <button
                    type="button"
                    onClick={() => setTriggerCreateProfile((c) => c + 1)}
                    className="flex items-center gap-1.5 bg-white px-3.5 py-1.5 rounded-xl hover:bg-sky-50 transition-all shadow-sm text-xs font-semibold cursor-pointer border border-white/80"
                    title={t('interviewLibrary.createProfile', 'Create Profile')}
                  >
                    <Plus size={15} className="text-fuchsia-500 stroke-[2.6]" />
                    <span className="inline-block bg-gradient-to-r from-sky-600 to-fuchsia-600 bg-clip-text text-transparent">
                      {t('interviewLibrary.createProfile', 'Create Profile')}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setTriggerAddQuestion((c) => c + 1)}
                    className="flex items-center gap-1.5 bg-white px-3.5 py-1.5 rounded-xl hover:bg-sky-50 transition-all shadow-sm text-xs font-semibold cursor-pointer border border-white/80"
                    title={t('questionBank.addQuestion', 'Add Question')}
                  >
                    <Plus size={15} className="text-fuchsia-500 stroke-[2.6]" />
                    <span className="inline-block bg-gradient-to-r from-sky-600 to-fuchsia-600 bg-clip-text text-transparent">
                      {t('questionBank.addQuestion', 'Add Question')}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex px-4 py-2.5 shrink-0 border-b border-slate-100 bg-slate-50/70">
            <div className="inline-flex gap-1 rounded-xl bg-slate-100/90 p-1">
            <button
              type="button"
              onClick={() => setActiveTab('profiles')}
              style={{
                fontFamily: FONT.fontFamily,
                ...(activeTab === 'profiles' ? { background: GRADIENT } : {}),
              }}
              className={tabButtonClass(activeTab === 'profiles')}
            >
              {t('interviewLibrary.interviewProfiles', 'Interview Profiles')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('bank')}
              style={{
                fontFamily: FONT.fontFamily,
                ...(activeTab === 'bank' ? { background: GRADIENT } : {}),
              }}
              className={tabButtonClass(activeTab === 'bank')}
            >
              {t('interviewLibrary.questionBank', 'Question Bank')}
            </button>
            </div>
          </div>

          {/* Tab Content Body - Scrollable Area */}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {activeTab === 'profiles' ? (
              <InterviewProfilesTab
                selectedCompany={selectedCompany}
                triggerCreate={triggerCreateProfile}
              />
            ) : (
              <QuestionBank
                embedded={true}
                triggerCreate={triggerAddQuestion}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
